import React, { useEffect, useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { useParams } from 'react-router';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { isComboboxDismissal } from '@/components/ui/multi-id-combobox';
import { ListFilter } from 'lucide-react';
import {
  CHECK_IN_STATUS_COLUMNS,
  expandCheckInStatusFilters,
  isAssignedToIncident,
  matchesCheckInStatusFilter,
} from './checkInStatus';
import { type ResourceRow } from './ResourceTabCard';
import { AssetsFilterBar } from './AssetsFilterBar';
import { DhsResourceAddDialog } from './DhsResourceAddDialog';
import { AssetsCheckInBoard } from './AssetsCheckInBoard';
import { DhsResourceTable } from './DhsResourceTable';
import { DhsResourceDetailsDialog } from './DhsResourceDetailsDialog';
import { ASSET_STATUS_FILTER_OPTIONS } from './resourceFilterOptions';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { USCGResourceKindOptions } from 'components/IncidentConfig/ResourceTypes';
import {
  type ResourceSet,
  type ResourceSetFilterSelection,
  usePaginatedResourceSets,
  useResourceSets,
} from '../../../IAP/hooks/resourceSetHooks';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import { resourceSetToRow } from './resourceSetToRow';
import { ResourceInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { CfaHeleneAssetItem } from '../NotificationsTab/CfaHeleneAssetItem';
import { getFacilityDemoPack } from '../NotificationsTab/facilityDemoPack';
import { isHeleneConeResourceSet } from '../NotificationsTab/memphisThreatNotification';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { openResourcePopupOnMap } from './dhsResourceDisplay';
import { useCfaDemoEnabled, useTropicalDemoKind } from 'utils/featureFlags';

const RESOURCES_PER_PAGE = 10;

/** Page numbers + ellipsis, matching react-paginate margin=2 / range=3. */
export function resourcePageItems(
  current: number,
  total: number
): Array<number | 'ellipsis'> {
  if (total <= 0) return [];
  const pages = new Set<number>();
  for (let i = 1; i <= Math.min(2, total); i++) pages.add(i);
  for (let i = Math.max(1, total - 1); i <= total; i++) pages.add(i);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push('ellipsis');
    items.push(sorted[i]);
  }
  return items;
}
const WORK_AVAILABILITY_FILTER_OPTIONS = [...CHECK_IN_STATUS_COLUMNS];
const REQUEST_STATUS_FILTER_OPTIONS = ['Requested', 'Not Requested'];

function resourceMatchesBoardFilters(
  resourceSet: ResourceSet,
  filterSelection: ResourceSetFilterSelection,
  searchTerm: string,
  aors: { id: string; name?: string }[],
  cfaEnabled: boolean
): boolean {
  if (!cfaEnabled && isHeleneConeResourceSet(resourceSet)) return false;
  const query = searchTerm.trim().toLowerCase();
  if (query && !resourceSet.name.toLowerCase().includes(query)) return false;
  if (
    filterSelection.kind.length > 0 &&
    !filterSelection.kind.includes(resourceSet.kind)
  ) {
    return false;
  }
  if (filterSelection.aor.length > 0) {
    const aorName = aors.find((aor) => aor.id === resourceSet.aor)?.name;
    if (!aorName || !filterSelection.aor.includes(aorName)) return false;
  }
  if (filterSelection.incident.length > 0) {
    const incidentIds = [
      ...(resourceSet.incident_details?.map((incident) => incident.id) ?? []),
      ...(resourceSet.incidents ?? []),
    ];
    if (!filterSelection.incident.some((id) => incidentIds.includes(id))) {
      return false;
    }
  }
  if (filterSelection.workAvailability.length > 0) {
    const status = resourceSet.current_status || resourceSet.status;
    if (
      !matchesCheckInStatusFilter(
        status,
        filterSelection.workAvailability,
        isAssignedToIncident(resourceSet)
      )
    ) {
      return false;
    }
  }
  if (filterSelection.requestStatus.length > 0) {
    const label = resourceSet.in_request ? 'Requested' : 'Not Requested';
    if (!filterSelection.requestStatus.includes(label)) return false;
  }
  if (filterSelection.assetStatus.length > 0) {
    if (!filterSelection.assetStatus.includes(resourceSet.asset_status || '')) {
      return false;
    }
  }
  return true;
}

const ResourceView: React.FC<{
  showAddAsset: boolean;
  setShowAddAsset: (show: boolean) => void;
  handleResourceUpdate: (updatedResource: Partial<ResourceInIncident>) => void;
  searchTerm: string;
}> = ({ showAddAsset, setShowAddAsset, searchTerm }) => {
  const facilityPack = getFacilityDemoPack(useTropicalDemoKind());
  const aorText = useAorText();
  const facilityDemoEnabled = !!facilityPack;
  // The Helene stores are seeded into the real table, so turning the demo off
  // has to hide them here too — they keep coming back from the server.
  const cfaEnabled = useCfaDemoEnabled();
  const [view, setView] = useState<'table' | 'list' | 'board'>(
    facilityDemoEnabled ? 'list' : 'table'
  );
  const [selectedRow, setSelectedRow] = useState<ResourceRow | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view');
  const [openedInEdit, setOpenedInEdit] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { mapView } = useUscgMapContext();

  const groupId = useGroupGuid();
  // Fetch group members
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId);

  const kindFilterOptions = useMemo(
    () => USCGResourceKindOptions.map((option) => option.value),
    []
  );
  const aorFilterOptions = useMemo(
    () => aors.map((aor) => aor.name).filter(Boolean),
    [aors]
  );
  const incidentFilterOptions = useMemo(
    () =>
      incidents
        .filter((incident) => !!incident.id && !!incident.name)
        .map((incident) => ({ value: incident.id, label: incident.name })),
    [incidents]
  );

  const columns = useMemo(() => {
    const generatedColumns = [
      {
        key: 'name',
        label: 'Resource Name',
        filterOptions: [],
      },
      {
        key: 'kind',
        label: 'Kind',
        filterOptions: kindFilterOptions,
      },
      {
        key: 'aor',
        label: aorText('AOR'),
        filterOptions: aorFilterOptions,
      },
      {
        key: 'incident',
        label: 'Incident',
        filterOptions: incidentFilterOptions,
      },
      {
        key: 'workAvailability',
        label: 'Current Operational Period Work Availability',
        filterOptions: WORK_AVAILABILITY_FILTER_OPTIONS,
      },
      {
        key: 'requestStatus',
        label: 'Request Status',
        filterOptions: REQUEST_STATUS_FILTER_OPTIONS,
      },
      {
        key: 'assetStatus',
        label: 'Asset Status',
        filterOptions: ASSET_STATUS_FILTER_OPTIONS,
      },
    ];
    return generatedColumns;
  }, [aorFilterOptions, incidentFilterOptions, kindFilterOptions, aorText]);

  // In the incident workspace (/iap/:incidentId) default the Incident filter to the current incident so the table
  // opens scoped to it. The raw route param (not the Redux fallback) keeps the corporate/map view unfiltered.
  const { incidentId: routeIncidentId } = useParams<{ incidentId: string }>();
  const [filterSelection, setFilterSelection] =
    useState<ResourceSetFilterSelection>({
      name: [],
      kind: [],
      aor: [],
      incident: routeIncidentId ? [routeIncidentId] : [],
      workAvailability: [],
      requestStatus: [],
      assetStatus: [],
      resultsPerPage: RESOURCES_PER_PAGE,
      pageNumber: 1,
    });

  const { data: resourceSetsPage } = usePaginatedResourceSets({
    ...filterSelection,
    workAvailability: expandCheckInStatusFilters(
      filterSelection.workAvailability
    ),
  });
  const { data: allResourceSets = [] } = useResourceSets();
  const { data: automationRules = [] } = useOrgEventAutomationRules();

  useEffect(() => {
    const nextName = searchTerm.trim() ? [searchTerm.trim()] : [];
    setFilterSelection((prev) => {
      const same =
        prev.name.length === nextName.length &&
        prev.name.every((value, index) => value === nextName[index]);
      if (same) return prev;
      return { ...prev, name: nextName, pageNumber: 1 };
    });
  }, [searchTerm]);

  const filteredResourceSets = useMemo(() => {
    const source = facilityPack
      ? allResourceSets.filter(facilityPack.isResourceSet)
      : (resourceSetsPage?.rows ?? []);
    const query = searchTerm.toLowerCase();
    return source.filter(
      (resourceSet) =>
        (cfaEnabled || !isHeleneConeResourceSet(resourceSet)) &&
        resourceSet.name.toLowerCase().includes(query)
    );
  }, [allResourceSets, cfaEnabled, facilityPack, resourceSetsPage, searchTerm]);

  // The server counts the seeded rows it returned, so the totals have to drop
  // them as well or the pager offers pages that render short.
  const hiddenCfaCount = useMemo(
    () =>
      cfaEnabled ? 0 : allResourceSets.filter(isHeleneConeResourceSet).length,
    [allResourceSets, cfaEnabled]
  );

  const pagedResourceSets = useMemo(() => {
    if (!facilityDemoEnabled) return filteredResourceSets;
    const page = filterSelection.pageNumber ?? 1;
    const perPage = filterSelection.resultsPerPage ?? RESOURCES_PER_PAGE;
    const start = (page - 1) * perPage;
    return filteredResourceSets.slice(start, start + perPage);
  }, [
    facilityDemoEnabled,
    filterSelection.pageNumber,
    filterSelection.resultsPerPage,
    filteredResourceSets,
  ]);

  const memoizedResources = useMemo<ResourceRow[]>(
    () =>
      pagedResourceSets.map((resourceSet) =>
        resourceSetToRow(resourceSet, groupMembers, automationRules)
      ),
    [automationRules, groupMembers, pagedResourceSets]
  );

  const boardResourceSets = useMemo(() => {
    const source = facilityPack
      ? allResourceSets.filter(facilityPack.isResourceSet)
      : allResourceSets;
    return source.filter((resourceSet) =>
      resourceMatchesBoardFilters(
        resourceSet,
        filterSelection,
        searchTerm,
        aors,
        cfaEnabled
      )
    );
  }, [
    allResourceSets,
    aors,
    cfaEnabled,
    facilityPack,
    filterSelection,
    searchTerm,
  ]);

  const boardRows = useMemo<ResourceRow[]>(
    () =>
      boardResourceSets.map((resourceSet) =>
        resourceSetToRow(resourceSet, groupMembers, automationRules)
      ),
    [automationRules, boardResourceSets, groupMembers]
  );

  useEffect(() => {
    if (!selectedRow?.resource_set_id) return;
    const mapped = [...memoizedResources, ...boardRows].find(
      (row) => row.resource_set_id === selectedRow.resource_set_id
    );
    if (!mapped) return;
    setSelectedRow(mapped);
  }, [boardRows, memoizedResources, selectedRow?.resource_set_id]);

  const resourceBySetId = useMemo(() => {
    const map = new Map<string, ResourceSet>();
    for (const resourceSet of [...filteredResourceSets, ...boardResourceSets]) {
      map.set(resourceSet.resource_set_id, resourceSet);
    }
    return map;
  }, [boardResourceSets, filteredResourceSets]);

  const handleFilterChange = (filterType: string, values: string[]) => {
    if (!(filterType in filterSelection)) {
      return;
    }
    setFilterSelection((prev) => ({
      ...prev,
      [filterType]: values,
      pageNumber: 1,
    }));
  };

  // The incident workspace scopes the list to its own incident, so clearing
  // returns to that scope rather than to every asset in the org.
  const clearFilters = () => {
    setFilterSelection((prev) => ({
      ...prev,
      name: [],
      kind: [],
      aor: [],
      incident: routeIncidentId ? [routeIncidentId] : [],
      workAvailability: [],
      requestStatus: [],
      assetStatus: [],
      pageNumber: 1,
    }));
  };

  const hasActiveFilters = columns.some(({ key }) => {
    const value = filterSelection[key as keyof ResourceSetFilterSelection];
    if (key === 'incident' && routeIncidentId) return false;
    return Array.isArray(value) && value.length > 0;
  });

  const updateCurrentPage = (pageNumber: number) => {
    setFilterSelection((prev) => ({
      ...prev,
      pageNumber,
      resultsPerPage: RESOURCES_PER_PAGE,
    }));
  };

  const openDetails = (row: ResourceRow) => {
    setSelectedRow(row);
    setDialogMode('view');
    setOpenedInEdit(false);
  };

  const startEdit = (row: ResourceRow) => {
    setSelectedRow(row);
    setDialogMode('edit');
    setOpenedInEdit(true);
  };

  const handleMapClick = (row: ResourceRow) => {
    openResourcePopupOnMap({
      row,
      mapView,
      resourceBySetId,
      aors,
    });
  };

  const closeDialog = () => {
    setSelectedRow(null);
    setDialogMode('view');
    setOpenedInEdit(false);
  };

  const perPage = filterSelection.resultsPerPage ?? RESOURCES_PER_PAGE;
  const totalPages = facilityDemoEnabled
    ? Math.ceil(filteredResourceSets.length / perPage)
    : hiddenCfaCount > 0
      ? Math.ceil(
          Math.max(0, (resourceSetsPage?.totalItems ?? 0) - hiddenCfaCount) /
            perPage
        )
      : (resourceSetsPage?.totalPages ?? 0);
  const currentPage = facilityDemoEnabled
    ? (filterSelection.pageNumber ?? 1)
    : resourceSetsPage?.pageNumber || 1;

  return (
    <>
      <div className="tw:min-w-0">
        <DhsResourceAddDialog
          open={showAddAsset}
          onOpenChange={setShowAddAsset}
          groupMembers={groupMembers}
        />
        <div className="tw:my-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
          <ToggleGroup
            variant="outline"
            size="sm"
            spacing={0}
            multiple={false}
            value={[view]}
            onValueChange={(next) => {
              const value = Array.isArray(next) ? next[0] : next;
              if (value === 'table' || value === 'list' || value === 'board') {
                setView(value);
              }
            }}
            aria-label="Resource view"
          >
            <ToggleGroupItem value="table">Table</ToggleGroupItem>
            <ToggleGroupItem value="list">List</ToggleGroupItem>
            <ToggleGroupItem value="board">Board</ToggleGroupItem>
          </ToggleGroup>
          <Popover
            open={filtersOpen}
            onOpenChange={(open, eventDetails) => {
              const fromCombobox =
                !open &&
                (eventDetails.reason === 'outside-press' ||
                  eventDetails.reason === 'focus-out') &&
                isComboboxDismissal(eventDetails.event);
              if (fromCombobox) {
                eventDetails.cancel();
                return;
              }
              setFiltersOpen(open);
            }}
          >
            <PopoverTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label="Filters"
                  aria-expanded={filtersOpen}
                  className="tw:relative"
                >
                  <ListFilter className="tw:h-4 tw:w-4" />
                  {hasActiveFilters ? (
                    <span
                      className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
                      aria-hidden
                    />
                  ) : null}
                </Button>
              }
            />
            <PopoverContent align="end" className="dark tw:w-80 tw:p-3">
              <AssetsFilterBar
                filters={(({
                  resultsPerPage: _rpp,
                  pageNumber: _pn,
                  ...rest
                }) => rest)(filterSelection)}
                onFilterChange={handleFilterChange}
                columns={columns}
                onClearAll={clearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </PopoverContent>
          </Popover>
        </div>
        {view === 'table' ? (
          <DhsResourceTable
            rows={memoizedResources}
            onSelectRow={openDetails}
            onEditRow={startEdit}
            onMapClick={handleMapClick}
          />
        ) : view === 'board' ? (
          <AssetsCheckInBoard
            rows={boardRows}
            resourceBySetId={resourceBySetId}
            onSelectRow={openDetails}
          />
        ) : (
          <div className="tw:min-w-0 tw:space-y-1.5">
            {memoizedResources.length === 0 ? (
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                No resources
              </p>
            ) : (
              memoizedResources.map((row) => {
                const resource = row.resource_set_id
                  ? resourceBySetId.get(row.resource_set_id)
                  : undefined;
                if (!resource) return null;
                return (
                  <CfaHeleneAssetItem
                    key={row.id}
                    resource={resource}
                    row={row}
                    includeChecklist={false}
                    showThreatChecklistCoverage
                    onMapClick={() => handleMapClick(row)}
                    onEditClick={() => startEdit(row)}
                  />
                );
              })
            )}
          </div>
        )}
        {view !== 'board' && totalPages > 1 && (
          <div className="dark tw:mt-2 tw:text-foreground">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href={void 0}
                    onClick={(event) => {
                      event.preventDefault();
                      if (currentPage > 1) {
                        updateCurrentPage(currentPage - 1);
                      }
                    }}
                    aria-disabled={currentPage <= 1}
                    className={
                      currentPage <= 1
                        ? 'tw:pointer-events-none tw:opacity-50'
                        : undefined
                    }
                  />
                </PaginationItem>
                {resourcePageItems(currentPage, totalPages).map(
                  (item, index) =>
                    item === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href={void 0}
                          isActive={item === currentPage}
                          onClick={(event) => {
                            event.preventDefault();
                            updateCurrentPage(item);
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                )}
                <PaginationItem>
                  <PaginationNext
                    href={void 0}
                    onClick={(event) => {
                      event.preventDefault();
                      if (currentPage < totalPages) {
                        updateCurrentPage(currentPage + 1);
                      }
                    }}
                    aria-disabled={currentPage >= totalPages}
                    className={
                      currentPage >= totalPages
                        ? 'tw:pointer-events-none tw:opacity-50'
                        : undefined
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
      <DhsResourceDetailsDialog
        row={selectedRow}
        open={!!selectedRow}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        mode={dialogMode}
        onModeChange={setDialogMode}
        openedInEdit={openedInEdit}
        groupMembers={groupMembers}
        onRowPatch={(patch) => {
          setSelectedRow((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
      />
    </>
  );
};

export default ResourceView;
